import React from "react";
import * as io from "io-ts";
import { css } from "@linaria/core";
import { nullable } from "diagnostics-lib/io";
import { FilterWrapper, buildOptions, useSubStateSetter } from "@ui/Filter";
import FilterSelect from "@ui/Filter/FilterSelect";
import t from "../../../../i18n";
import Reset from "@ui/Filter/Reset";
import { useCategories } from "../../../../api/useCategories";
import { useFailuresFilter } from "../../../../api/useReporting";

const FilterValues = io.strict({
  failureCategoryIds: io.array(nullable(io.string)),
  systemIds: io.array(nullable(io.string)),
  degradationTypeIds: io.array(nullable(io.string)),
  equipmentTypeIds: io.array(nullable(io.string)),
});

type FilterValues = io.TypeOf<typeof FilterValues>;

const DEFAULT_FILTER_VALUES: FilterValues = {
  failureCategoryIds: [],
  systemIds: [],
  degradationTypeIds: [],
  equipmentTypeIds: [],
};

const FilterContainer = css`
  align-items: center;
  column-gap: 24px;
  display: flex;
  flex-grow: 1;
`;

function ReportingDiagnosisFilters() {
  const systems = useCategories("SYSTEM");
  const categories = useCategories("FAILURE_CATEGORY");
  const degradationTypes = useCategories("DEGRADATION_TYPE");
  const equipmentTypes = useCategories("EQUIPMENT_TYPE");
  const [filterValues, setFilterValues] = useFailuresFilter({});

  const [
    systemOptions = [],
    categoryOptions = [],
    degradationTypeOptions = [],
    equipmentTypeOptions = [],
  ] = React.useMemo(
    () =>
      [systems, categories, degradationTypes, equipmentTypes].map(categoryList => [
        ...buildOptions(
          categoryList.reduce<{ label: string; value: string }[]>(
            (acc, name) => [...acc, { label: name, value: name }],
            [],
          ),
        ),
      ]),
    [categories, degradationTypes, equipmentTypes, systems],
  );

  return (
    <div className={FilterContainer}>
      <FilterWrapper>
        <FilterSelect<string | null>
          title={t("reporting.filters.failure.category")}
          options={categoryOptions}
          filtered
          values={filterValues["failureCategoryIds"]}
          setValues={useSubStateSetter(setFilterValues, "failureCategoryIds")}
        />
        <FilterSelect<string | null>
          title={t("reporting.filters.failure.system")}
          options={systemOptions}
          filtered
          values={filterValues["systemIds"]}
          setValues={useSubStateSetter(setFilterValues, "systemIds")}
        />
        <FilterSelect<string | null>
          title={t("reporting.filters.failure.degradationType")}
          options={degradationTypeOptions}
          filtered
          values={filterValues["degradationTypeIds"]}
          setValues={useSubStateSetter(setFilterValues, "degradationTypeIds")}
        />
        <FilterSelect<string | null>
          title={t("reporting.filters.failure.equipmentType")}
          options={equipmentTypeOptions}
          filtered
          values={filterValues["equipmentTypeIds"]}
          setValues={useSubStateSetter(setFilterValues, "equipmentTypeIds")}
        />
        <Reset
          filterValues={filterValues}
          setFilterValues={setFilterValues}
          defaultFilterValues={DEFAULT_FILTER_VALUES}
        />
      </FilterWrapper>
    </div>
  );
}

export default React.memo(ReportingDiagnosisFilters);
